import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { ScreenLayoutContext } from "./context";
import { useScreenLayoutStoreInstance } from "./screen-layout-store";
import { ScreenLayoutWarnings } from "./screen-layout-warnings";
import { screenLayoutContainerVariants } from "./screen-layout.variants";
import type { ScreenLayoutProps } from "./types";

/**
 * ScreenLayout — web.
 *
 * Fixed-viewport model, matching native: the root is a flex column sized
 * to the viewport minus any in-flow app chrome above it (`--app-chrome-height`,
 * 0px when the host sets nothing). The Body is a contained `overflow-auto`
 * scroll region between a fixed Header and Actions, so body content never
 * slides under the chrome — the same behavior as the native ScrollView.
 *
 * The root must not be a flex item on a column main axis: `flex-1` would
 * resolve its main size from `flex-basis: 0%` and discard the height below.
 */
export const ScreenLayout = ({
  background = "default",
  className,
  testID,
  children,
}: ScreenLayoutProps) => {
  const store = useScreenLayoutStoreInstance();

  return (
    <ScreenLayoutContext.Provider value={store}>
      <Box
        {...platformProps({ testID, slot: "screen-layout" })}
        className={cn(
          "flex h-[calc(100dvh_-_var(--app-chrome-height,0px))] min-h-0 flex-col",
          screenLayoutContainerVariants({ background }),
          className,
        )}
      >
        {children}
      </Box>
      <ScreenLayoutWarnings store={store} />
    </ScreenLayoutContext.Provider>
  );
};

ScreenLayout.displayName = "ScreenLayout";
